import { useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';

import lineTypeIcon from '@atom/components/common/svgIcons/lineTypeIcon.svg';
import lineTypeIconBlue from '@atom/components/common/svgIcons/lineTypeIconBlue.svg';
import { Icon, Modal, TextField } from '@atom/mui';
import colors from '@atom/styles/colors';
import {
  LocationType,
  SchemaSavePayload,
  SchemaTree,
} from '@atom/types/schema';
import { getColorSwatchList } from '@atom/utilities/colorUtilities';
import { getSchemaIconsList } from '@atom/utilities/markerUtilities';
import { isNilOrEmpty } from '@atom/utilities/validationUtilities';

import classes from './addEditSchemaModal.module.css';

const styles = {
  modalContent: { padding: '2rem' },
};

interface Props {
  open: boolean;
  onClose: () => void;
  handleSave: (payload: SchemaSavePayload) => void;
  schema?: Partial<SchemaTree>;
}

const AddEditSchemaModal = ({ open, onClose, handleSave, schema }: Props) => {
  const [name, setName] = useState<string>(schema?.name || '');
  const [colorId, setColorId] = useState<number>(schema?.colorId || 0);
  const [markerId, setMarkerId] = useState<number>(schema?.markerId || 0);
  const [isMaterial, setIsMaterial] = useState<boolean>(
    schema?.isMaterial || false,
  );
  const [locationType, setLocationType] = useState<LocationType>(
    schema?.locationType || LocationType.POINT,
  );

  const editMode = !isNilOrEmpty(schema);

  const resetState = () => {
    setName(schema?.name || '');
    setColorId(schema?.colorId || 0);
    setMarkerId(schema?.markerId || 0);
    setIsMaterial(schema?.isMaterial || false);
    setLocationType(schema?.locationType || LocationType.POINT);
  };

  useEffect(() => {
    resetState();
  }, [open, schema]);

  const onConfirm = () => {
    handleSave({
      name,
      colorId,
      markerId,
      locationType: isMaterial ? null : locationType,
      isMaterial,
    });
  };

  const handleMarkerClick = (id: number) => {
    setMarkerId(Number(id));
    setColorId(Number(id));
  };

  const getColorIcons = () => {
    const colorSwatch = getColorSwatchList();

    return colorSwatch.map((swatch: string, index: number) => {
      return (
        <div
          key={`${swatch}-${index}`}
          className={clsx(
            classes.schemaIcon,
            index === colorId && classes.selected,
          )}
          onClick={() => handleMarkerClick(index)}
        >
          <Icon color={swatch} style={{ fontSize: '30px' }}>
            lens
          </Icon>
        </div>
      );
    });
  };

  const getSchemaIcons = () => {
    const schemaIconsList = getSchemaIconsList();

    return schemaIconsList.map((icon: string, index: number) => {
      return (
        <div
          key={index}
          className={clsx(
            classes.schemaIcon,
            index === markerId && classes.selected,
          )}
          id={index.toString()}
          onClick={() => handleMarkerClick(index)}
        >
          <img id={index.toString()} src={icon} />
        </div>
      );
    });
  };

  const swatches = useMemo(() => {
    return isMaterial ? getColorIcons() : getSchemaIcons();
  }, [isMaterial, colorId, markerId]);

  const title = editMode ? 'Edit Inventory Type' : 'Add Inventory Type';
  const confirmText = editMode ? 'Save' : 'Create';

  const locationIconColor = isMaterial
    ? colors.neutral.gray
    : colors.brand.blue;

  const materialIconColor = isMaterial
    ? colors.brand.blue
    : colors.neutral.gray;

  const pointIconColor =
    locationType === LocationType.POINT
      ? colors.brand.blue
      : colors.neutral.gray;
  const lineStringIcon =
    locationType === LocationType.LINE_STRING ? lineTypeIconBlue : lineTypeIcon;

  const colorSectionTitle = isMaterial ? 'Color:' : 'Color and Map Marker:';

  const disabled = isNilOrEmpty(name.trim());

  return (
    <Modal
      title={title}
      open={open}
      onCancel={onClose}
      onConfirm={onConfirm}
      onExited={resetState}
      confirmButtonText={confirmText}
      contentStyle={styles.modalContent}
      disabled={disabled}
    >
      <TextField
        label="Inventory Type Name"
        value={name}
        id="name"
        onChange={event => setName(event?.target?.value)}
        variant="standard"
      />
      <div className={classes.sectionTitle}>Type:</div>
      <div className={classes.typeContainer}>
        <div
          className={clsx(classes.typeTile, !isMaterial && classes.selected)}
          onClick={() => setIsMaterial(!isMaterial)}
        >
          <Icon color={locationIconColor}>map</Icon>
          <div className={classes.typeTileText}>Location-based</div>
        </div>
        <div
          className={clsx(classes.typeTile, isMaterial && classes.selected)}
          onClick={() => setIsMaterial(!isMaterial)}
        >
          <Icon color={materialIconColor}>monetization_on</Icon>
          <div className={classes.typeTileText}>Equipment and Material</div>
        </div>
      </div>
      {!isMaterial && (
        <>
          <div className={classes.sectionTitle}>Location Type:</div>
          <div className={classes.typeContainer}>
            <div
              className={clsx(
                classes.typeTile,
                locationType === LocationType.POINT && classes.selected,
              )}
              onClick={() => setLocationType(LocationType.POINT)}
            >
              <Icon color={pointIconColor}>location_on</Icon>
              <div className={classes.typeTileText}>Point</div>
            </div>
            <div
              className={clsx(
                classes.typeTile,
                locationType === LocationType.LINE_STRING && classes.selected,
              )}
              onClick={() => setLocationType(LocationType.LINE_STRING)}
            >
              <img src={lineStringIcon} />
              <div className={classes.typeTileText}>Line</div>
            </div>
          </div>
        </>
      )}
      <div className={classes.sectionTitle}>{colorSectionTitle}</div>
      <div className={classes.colorContainer}>{swatches}</div>
    </Modal>
  );
};

export default AddEditSchemaModal;
